import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormFields } from '@src/components/FormFields';
import Layout from '~/components/Layout';
import notify from '~/components/Notify';
import { useAuth } from '~/routes/components/AuthProvider'
import { useForm, useFieldArray } from 'react-hook-form';
import { useMutation } from 'react-query';
import TradingSlotSettingsService from '~/api/services/tradingSlotSettings';
import { Form, Accordion, Icon, Tab, Button, Popup, Grid, Segment } from 'semantic-ui-react';
import {
  TabPaneStyled,
  StyledDeleteButton,
  StyledAccordionTitle
} from './style';
import InfoPopup from '~/components/InfoPopup';
import TradingRoom from './TradingRoom';
import { handleErrors } from '~/utils/helpers';

const TradingSlot: React.FC = () => {
  const { t } = useTranslation();
  const { systemData } = useAuth();
  const [activeAccordionIndex, setActiveAccordionIndex] = useState(-1);
  const languages = systemData?.languages;
  const [isBusy, setIsBusy] = useState(false);
  const [showData, setShowData] = useState(null);
  const { handleSubmit, control, setValue, getValues, watch, setError, formState } = useForm({
    mode: 'onSubmit'
  });
  const [tabIndex, setTabIndex] = useState(-1)

  const tradingVolumeTypes = useFieldArray({
    control,
    name: 'tradingVolumeTypes'
  });
  const tradingSlotTerms = useFieldArray({
    control,
    name: 'tradingSlotTerms'
  });
  const texts = useFieldArray({
    control,
    name: 'texts'
  });

  useEffect(() => {
    if (Object.keys(formState.errors).length > 0) {
      const tabIndex = formState.errors?.texts ? formState.errors?.texts[0]?.languages?.findIndex(lang => !!lang) :
        formState.errors?.tradingSlotTerms ? formState.errors?.tradingSlotTerms[0]?.languages?.findIndex(lang => !!lang) : -1
      if (tabIndex >= 0) {
        setActiveAccordionIndex(0)
        setTabIndex(tabIndex)
      }
      notify(t('Please fill in all required fields'), 'error')
    }
  }, [formState.errors, formState.isSubmitted])

  //TODO Separate trading volume active language indexes per trading volume object
  const [tradingVolumeActiveIndexes, setTradingVolumeActiveIndexes] = useState([]);
  const wholeNumberErrorMessage = t('Please enter whole number');
  const watchFarmerOfferChange = watch('farmerOfferChange')

  useEffect(() => {
    fetchTradingData();
  }, [t]);

  useEffect(() => {
    //TODO Work with BE to create a better solution, i.e. return languages arrays from backend with id/label already present, even if there is no translation?
    if (languages) {
      if (texts) {
        texts.fields.forEach((text, textIndex) => {
          languages.forEach((lang, languageIndex) => {
            setValue(`texts[${textIndex}].languages[${languageIndex}].id`, languages[languageIndex].id);
          });
        });
      }

      if (tradingVolumeTypes) {
        tradingVolumeTypes.fields.forEach((volume, volumeIndex) => {
          languages.forEach((lang) => {
            const index = getValues(`tradingVolumeTypes[${volumeIndex}].languages`).findIndex(termLang => termLang?.id === lang.id)
            if (index >= 0) {
              setValue(`tradingVolumeTypes[${volumeIndex}].languages[${index}].id`, lang.id);
            }
          });
        });
      }

      if (tradingSlotTerms) {
        tradingSlotTerms.fields.forEach((term, termIndex) => {
          languages.forEach((lang) => {
            const index = getValues(`tradingSlotTerms[${termIndex}].languages`).findIndex(termLang => termLang?.id === lang.id)
            if (index >= 0) {
              setValue(`tradingSlotTerms[${termIndex}].languages[${index}].id`, lang.id);
            }
          });
        });
      }
    }
  }, [languages, texts, tradingVolumeTypes, tradingSlotTerms]);

  async function fetchTradingData() {
    setIsBusy(true);
    try {
      const showResponse = await TradingSlotSettingsService.getTradingSlotSettingsOptions();
      const editResponse = await TradingSlotSettingsService.getEditTradingSlotSettings();
      const editResponseData = editResponse.data;
      for (const key in editResponseData) {
        if (Array.isArray(editResponseData[key])) {
          setValue(key, [...editResponseData[key]]);
        } else {
          setValue(key, editResponseData[key]);
        }
      }
      const tradingVolumeActiveIndexes = []
      editResponseData.tradingVolumeTypes.map(tradingSlotTerm => {
        tradingVolumeActiveIndexes.push({
          tradingVolumeId: tradingSlotTerm.id,
          index: 0
        })
      })
      setTradingVolumeActiveIndexes(tradingVolumeActiveIndexes)
      setShowData(showResponse.data);
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false);
  }

  function addTradingVolumeType() {
    const tvtl = languages.map((lang, index) => {
      return {
        id: lang.id,
        name: ''
      }
    })
    const tvt = {
      percent: 0,
      status: 1,
      languages: tvtl
    }
    setTradingVolumeActiveIndexes([...tradingVolumeActiveIndexes, { tradingVolumeId: Math.random(), index: 0 }])
    tradingVolumeTypes.append(tvt);
  }

  function removeTradingVolumeType(index) {
    setTradingVolumeActiveIndexes(tradingVolumeActiveIndexes.filter(indexTra => indexTra !== index))
    tradingVolumeTypes.remove(index);
  }

  function renderTradingVolumeTypesPane(language, index, volumeIndex) {
    //@ts-ignore
    const langArray = tradingVolumeTypes?.fields[volumeIndex]?.languages
    const indexLang = langArray?.findIndex(field => field.id === language.id)
    return (
      <TabPaneStyled
        active={tradingVolumeActiveIndexes[volumeIndex]?.index === index}
        key={'tvtp' + index + volumeIndex}
      >
        <FormFields.Input
          label={t('Name')}
          placeholder={t('Name')}
          rules={{
            required: { value: true, message: t('Please enter trading volume type name') }
          }}
          control={control}
          name={`tradingVolumeTypes[${volumeIndex}].languages[${indexLang}].name`}
        />
      </TabPaneStyled>
    )
  }

  function tradingVolumeTypesLanguagePanes(volumeIndex) {
    const tabs = languages?.map((language, index) => {
      return {
        menuItem: language?.label || null,
        render: () => renderTradingVolumeTypesPane(language, index, volumeIndex)
      }
    })
    return tabs
  }

  function renderTradingVolumeTypes() {
    return (
      <>
        <Form.Field required style={{ display: 'flex', alignItems: 'baseline' }}>
          <label style={{ marginRight: 10 }}>{t('Trading volume types')}</label>
          <Popup
            content={t('Add new trading volume type')}
            trigger={<Button
              onClick={() => addTradingVolumeType()}
              primary
              size='mini'
              type={'button'}
              icon='plus'
            />
            }
          />
        </Form.Field>
        {tradingVolumeTypes.fields.map((type, index) => {
          return <Segment key={type.id} style={{ position: 'relative' }}>
            <Tab menu={{ secondary: true, pointing: true }}
              style={{ marginBottom: '0.5rem' }}
              activeIndex={tradingVolumeActiveIndexes[index] ? tradingVolumeActiveIndexes[index].index : 0}
              renderActiveOnly={true}
              onTabChange={(_, data) => {
                const tradingVolumeActiveIndexesTemp = [...tradingVolumeActiveIndexes]
                tradingVolumeActiveIndexesTemp[index].index = data.activeIndex
                setTradingVolumeActiveIndexes(tradingVolumeActiveIndexesTemp)
              }}
              panes={tradingVolumeTypesLanguagePanes(index)} />
            <FormFields.Input
              control={control}
              typeInput={'controled'}
              allowDecimals={true}
              rules={{
                required: {
                  value: true,
                  message: t('Please enter trading volume type percent')
                },
                min: {
                  value: 0,
                  message: t('Minimum value enter must be 0')
                }
              }}
              placeholder={t('Percent')}
              label={t('Percent')}
              name={`tradingVolumeTypes[${index}].percent`}
            />
            {index !== 0 && <StyledDeleteButton
              onClick={() => removeTradingVolumeType(index)}
              size={'small'}
              basic
              color='red'
              type={'button'}
              icon='trash' />}
          </Segment>
        })}
      </>
    )
  }

  const { isLoading: isTradingSlotSettingsStoreLoading, mutate: storeTradingSlotSettings } = useMutation(
    'tradingSlotSettings',
    TradingSlotSettingsService.createTradingSlotSettings,
    {
      onSuccess: () => {
        notify(t('Successfully stored trading slot settings'), 'success');
      },
      onError: (error: any) => {
        handleErrors(error)
      }
    }
  );

  const onSubmit = (data) => {
  //  const tradingRoomDirty = Object.keys(formState.dirtyFields).find(key => key === 'tradingSlotTerms')
    data.tradingVolumeTypes?.forEach(item => {
      if (item.percent.includes(',')) {
        item.percent = item.percent.replace(',', '.')
      }
    })
    const volumePercentagesSum = data.tradingVolumeTypes.reduce((total, item) => parseFloat(item.percent) + total, 0);
    if (volumePercentagesSum !== 100) {
      setError('tradingVolumeTypes[0].percent', { type: 'manual', message: t('Sum of Trading Volume Type percentages must be 100') });
      return;
    }

    data.tradingSlotTerms[0]?.languages.forEach(type => {
      delete type.code
      delete type.label
    })

    if (data.standardPrice?.includes(',')) {
      data.standardPrice = data.standardPrice?.replace(',', '.')
    }

    try {
      storeTradingSlotSettings(data);
      //if (tradingRoomDirty) {
      //  fetchSystemData()
      // }
    } catch (err) {
      notify('onSubmit error', 'error');
    }
  };

  function getLabel(label, infoMessage) {
    return <label style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
      <div>{label}</div>
      <InfoPopup
        content={infoMessage}
        styleIcon={{ fontSize: '1.2em', cursor: 'pointer' }}
      />
    </label>
  }

  function handleTradingRoomHeaderClick(e, titleProps) {
    const { index } = titleProps
    setActiveAccordionIndex(activeAccordionIndex === index ? -1 : index);
  }

  function renderForm() {
    return <>
      <div style={{ marginBottom: '2rem' }}>{renderTradingVolumeTypes()}</div>
      <Segment>
        <Form.Group widths={'equal'} style={{ marginBottom: '2rem' }}>
          <Form.Field required>
            <FormFields.Select
              control={control}
              label={getLabel(
                t('Amount offering type'),
                t('Please select the options that can be used for the submission of trading amounts within a trading slot. You can select one, two or all three options.')
              )}
              multiple
              placeholder={t('Amount offering type')}
              name={'amountOfferingTypes'}
              options={showData?.amountOfferingTypes.map(type => ({ value: type.key, text: type.label }))}
            />
          </Form.Field>
          <Form.Field required>
            <FormFields.Select
              control={control}
              label={getLabel(
                t('Methods for trading slots'),
                t('With the quota allocation principle, the supply contingents are collected and, once the protection has been concluded, \n\
                distributed as a percentage according to the respective supply contingent of the farmer.  \n\
                With the priority principle, the principle applies that the first offer quotas received by farmers are given priority.')
              )}
              multiple
              placeholder={t('Methods')}
              name={'tradingSlotMethods'}
              options={showData?.tradingSlotMethods.map(method => ({ value: method.key, text: method.label }))}
            />
          </Form.Field>
        </Form.Group>
        <Grid columns={2} stackable style={{ marginBottom: '2rem' }}>
          <Grid.Column>
            <FormFields.Checkbox
              control={control}
              label={getLabel(
                t('Use minimum contingent?'),
                t('Do you want to use the minimum contingent for farmer users?')
              )}
              checkboxValue={1}
              name={'minContingentOffer'}
            />
            <FormFields.Checkbox
              control={control}
              label={getLabel(
                t('Consider the minimum contingent of sub-suppliers?'),
                t('Should the minimum contingent of assigned subcontractors be included when submitting offers?')
              )}
              checkboxValue={1}
              name={'useMinimumContingentSum'}
            />
            <FormFields.Checkbox
              control={control}
              label={getLabel(
                t('Individual offers for sub suppliers?'),
                t('Should farmer users be allowed to submit individual offering contingencies \n\
                for each of their main and subcontractors as part of the offering contingency process?')
              )}
              checkboxValue={1}
              name={'individualSubSupplierOffer'}
            />
            <FormFields.Checkbox
              control={control}
              label={getLabel(
                t('Hide prices from farmers?'),
                t('Do you want to show the calculated total price (fixed price amount x fixed price) for the farmer users?')
              )}
              checkboxValue={1}
              name={'hiddenPrices'}
            />
            <FormFields.Checkbox
              control={control}
              label={getLabel(
                t('Allow farmers to subsequently change offers?'),
                t('Do you want to allow farmers to change their already made offers afterwards within a trading slot (in "Live" status)?')
              )}
              checkboxValue={1}
              name={'farmerOfferChange'}
            />
            <FormFields.Checkbox
              control={control}
              label={getLabel(
                t('Show prices only when the trading slot is live?'),
                t('If you activate this configuration, farmers can only see the prices when the trading slot goes into "Live" status. In the status "Planned" and "Defined" no fixed prices are then displayed.')
              )}
              defaultValue={0}
              checkboxValue={1}
              name={'showPricesInLiveStatus'}
            />
          </Grid.Column>
          <Grid.Column>
            <FormFields.Checkbox
              control={control}
              label={getLabel(
                t('Do farmers see trading slots in "Defined" status?'),
                t('Should farmer users be able to see the trading slot already in Defined status in their dashboard?')
              )}
              checkboxValue={1}
              name={'showDefinedTradingSlots'}
            />
            <FormFields.Checkbox
              control={control}
              label={getLabel(
                t('Show deactivated trading months?'),
                t('Should the trading months that have been disabled also be displayed to farmer users within a trading slot?')
              )}
              checkboxValue={1}
              name={'showDeactivatedMonths'}
            />
            <FormFields.Checkbox
              control={control}
              label={getLabel(
                t('Deactivate months by default?'),
                t('Should the trading months be initially disabled by default? \n\
                This means that you must actively select the corresponding months for which quantities are to be offered. (Recommended!)')
              )}
              checkboxValue={1}
              name={'deactivatedMonthsOnCreate'}
            />
            <FormFields.Checkbox
              control={control}
              label={getLabel(
                t('Use automatic ife prices transmission?'),
                t('Would you like to use the automatic transmission of fixed prices by ife? This requires prior consultation with ife.')
              )}
              checkboxValue={1}
              name={'ifeSubmitPrices'}
            />
            <FormFields.Checkbox
              control={control}
              label={getLabel(
                t('Allow exceeding max trading volume?'),
                t('Would you like to allow the maximum trading amount to be exceeded?')
              )}
              checkboxValue={1}
              name={'exceedingMaxAllowed'}
            />
          </Grid.Column>
        </Grid>
      </Segment>
      <Segment>
        {Boolean(watchFarmerOfferChange) && <FormFields.Input
          control={control}
          type={'number'}
          width={5}
          placeholder={t('Enter default amount changes number')}
          style={{ marginBottom: '2rem' }}
          label={t('Default amount changes')}
          name='defaultAmountChangesNumber'
          rules={{
            required: {
              value: true,
              message: t('Please enter default amount changes number')
            },
            min: {
              value: 1,
              message: t('Minimum value enter must be 1')
            },
            validate: v => (v - Math.floor(v) === 0) || wholeNumberErrorMessage
          }}
        />
        }
        <Form.Group widths={'equal'} style={{ marginBottom: '2rem' }}>
          <FormFields.Input
            control={control}
            typeInput={'controled'}
            allowDecimals={false}
            label={t('Offer amount')}
            rules={{
              min: {
                value: getValues('amountOfferStep') ? 1 : null,
                message: t('Minimum value enter must be 1')
              },
              validate: v => (v - Math.floor(v) === 0) || wholeNumberErrorMessage
            }}
            placeholder={t('Offer amount')}
            type='number'
            name='amountOfferStep'
          />
          <FormFields.Input
            control={control}
            typeInput={'controled'}
            allowDecimals={false}
            label={t('Minimum yearly amount')}
            rules={{
              min: {
                value: getValues('minYearlyAmount') ? 1 : null,
                message: t('Minimum value enter must be 1')
              },
              validate: v => (v - Math.floor(v) === 0) || wholeNumberErrorMessage
            }}
            placeholder={t('Minimum yearly amount')}
            type='number'
            name='minYearlyAmount'
          />
          <FormFields.Input
            typeInput={'controled'}
            control={control}
            rules={{
              min: {
                value: 0,
                message: t('Minimum value enter must be 0')
              }
            }}
            allowDecimals={true}
            name='standardPrice'
            label={t('Standard price')}
          />
        </Form.Group>
      </Segment>

      <Accordion styled style={{ width: '100%' }}>
        <Accordion.Title
          onClick={handleTradingRoomHeaderClick}
          index={0}
          active={activeAccordionIndex === 0}
          style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
          <StyledAccordionTitle>{t('Trading room')}</StyledAccordionTitle>
          <Icon name={activeAccordionIndex === 0 ? 'minus' : 'plus'} />
        </Accordion.Title>
        <Accordion.Content active={activeAccordionIndex === 0} content={<TradingRoom
          languages={languages}
          watch={watch}
          tabIndex={tabIndex}
          tradingSlotTerms={tradingSlotTerms}
          control={control}
          getValues={getValues}
          setValue={setValue}
          texts={texts}
        />} />
      </Accordion>
    </>
  }

  return <Layout
    isBusy={isBusy || isTradingSlotSettingsStoreLoading}
    title={t('Trading slot settings')}
    segmented
    action={t('Update')}
    onSubmit={handleSubmit(onSubmit)}
  >
    {!isBusy && renderForm()}
  </Layout>
};
export default TradingSlot;
